import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './course-contact.scss';
import useFormFocusHandlers from '../../hooks/useFormFocusHandlers';

// Utility function to throttle scroll events
const throttle = (func, delay) => {
  let lastCall = 0;
  return (...args) => {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return func(...args);
  };
};

const CourseContactForm = ({ courseName, isMobile }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    message: ''
  });
  
  const [showThankYou, setShowThankYou] = useState(false);
  const [isVisible, setIsVisible] = useState(true);  const [isFixed, setIsFixed] = useState(false);
  const formRef = useRef(null);
  const initialPosition = useRef(null);
  const textareaRef = useRef(null);
  
  // Use our custom hook for form focus handling
  const { 
    handleFocus, 
    handleBlur, 
    handleChange, 
    handleTextareaChange,
    handleFormContainerClick
  } = useFormFocusHandlers(textareaRef);
    // Handle scrolling and fixing the form
  useEffect(() => {
    // Skip all scroll/position handling if this is the mobile version
    if (isMobile) {
      setIsFixed(false);
      return; // Early return to avoid all scroll handling
    }
    
    // Function to calculate initial position
    const calculatePosition = () => {
      if (formRef.current) {
        // Record initial position
        const formRect = formRef.current.getBoundingClientRect();
        initialPosition.current = {
          top: formRect.top + window.scrollY,
          left: formRect.left
        };
      }
    };
      // Wait until content is loaded
    setTimeout(calculatePosition, 500); // Short delay to ensure content is rendered
    
    const handleScroll = () => {
      if (formRef.current && initialPosition.current) {
        // Check if textarea is currently focused - if so, don't adjust form position
        const isTextareaFocused = document.activeElement && 
          document.activeElement.tagName === 'TEXTAREA' && 
          document.body.classList.contains('textarea-focused');
        
        // Don't reposition the form if textarea is focused
        if (isTextareaFocused || window.__textareaActive) {
          return;
        }
        
        // Get current scroll position
        const scrollPos = window.scrollY;
        // Get viewport width
        const viewWidth = window.innerWidth;
        
        // If on mobile, don't apply fixed positioning logic at all
        if (viewWidth <= 992) {
          setIsFixed(false);
          
          // Remove any inline styles that might have been applied
          if (formRef.current) {
            formRef.current.style.position = '';
            formRef.current.style.top = '';
            formRef.current.style.left = '';
            formRef.current.style.width = '';
            formRef.current.style.transform = '';
          }
          
          return;
        }
        
        // Desktop behavior - continue with standard floating logic
        // Find footer element
        const footer = document.querySelector('.footer');
        
        // Default to standard behavior if footer not found
        if (!footer) {
          setIsFixed(scrollPos > initialPosition.current.top - 80);
          return;
        }
        
        // Get footer position
        const footerRect = footer.getBoundingClientRect();
        // Calculate the form height
        const formHeight = formRef.current.offsetHeight;
        
        // Get the absolute position of the footer top from the top of the document
        const footerTop = footerRect.top + window.scrollY;
        
        // Standard buffer space (gap between form and footer)
        const buffer = 50;
        
        // Beginning point for fixing the form (when to start floating)
        const startFixPoint = initialPosition.current.top - 20; // Start fixing slightly before reaching initial position
        
        // Calculate the point at which we need to adjust for footer proximity
        const footerAdjustPoint = footerTop - formHeight - buffer;
        
        // For the fixed position that starts at the top of the viewport with some padding
        const standardFixedTopPosition = 20;
        
        // Clear any previously set inline styles first
        formRef.current.style.position = '';
        formRef.current.style.top = '';
        formRef.current.style.left = '';
        formRef.current.style.width = '';
        
        // CASE 1: When we're scrolled past the initial position but not near footer
        if (scrollPos > startFixPoint && scrollPos < footerAdjustPoint) {
          // Standard fixed position at the top of the viewport
          setIsFixed(true);
          formRef.current.style.position = 'fixed';
          formRef.current.style.top = `${standardFixedTopPosition}px`;
          formRef.current.style.left = `${initialPosition.current.left}px`;
          formRef.current.style.width = '400px'; // Match the sidebar width
        }
        // CASE 2: When we're near the footer - position above it
        else if (scrollPos >= footerAdjustPoint) {
          // Calculate absolute position that would place the form above the footer
          const absoluteTopPosition = footerTop - formHeight - buffer;
          const relativeTopPosition = absoluteTopPosition - scrollPos;
          
          setIsFixed(true);
          formRef.current.style.position = 'fixed';
          formRef.current.style.top = `${relativeTopPosition}px`;
          formRef.current.style.left = `${initialPosition.current.left}px`;
          formRef.current.style.width = '400px'; // Match the sidebar width
        }
        // CASE 3: When we're at the top of the page, before initial position
        else {
          setIsFixed(false);
        }
      }
    };
    
    // Apply throttling to scroll handler for better performance
    const throttledScrollHandler = throttle(handleScroll, 50); // 50ms throttle
    
    window.addEventListener('scroll', throttledScrollHandler);
    window.addEventListener('resize', throttledScrollHandler);
    window.addEventListener('resize', calculatePosition);
    
    // Run initial position calculation
    calculatePosition();
    // Run initial scroll handler to set correct initial state
    handleScroll();
    
    return () => {      window.removeEventListener('scroll', throttledScrollHandler);
      window.removeEventListener('resize', throttledScrollHandler);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isMobile]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create submission data with course name included
    const submissionData = {
      ...formData,
      subject: `Question about ${courseName || 'course'}`
    };
    
    axios.post('https://contact.watchelm.com', submissionData)
      .then(response => {
        console.log('Form submitted successfully:', response.data);
        setShowThankYou(true);
      })
      .catch(error => {
        console.error('Error submitting form:', error);
        // Error handling could be added here
      });
  };  // When visibility changes, we need to recalculate positions
  useEffect(() => {
    // Skip for mobile version
    if (isMobile) {
      return;
    }
    
    if (isVisible && formRef.current) {
      // Trigger a scroll event to recalculate form position after visibility change
      setTimeout(() => {
        // Only dispatch scroll event if textarea is not active
        if (!window.__textareaActive && !document.body.classList.contains('textarea-focused')) {
          window.dispatchEvent(new Event('scroll'));
        }
      }, 50);
    }
  }, [isVisible, isMobile]);
  
  // Add special handling for this form to prevent position jumps when textarea is focused
  useEffect(() => {
    const handleTextareaFocusForForm = () => {
      // Store the form's current styles when textarea gets focus
      if (formRef.current && formRef.current.className.includes('fixed')) {
        // Add a data attribute to mark the form's position as locked
        formRef.current.setAttribute('data-position-locked', 'true');
        
        // Save current styles to restore later if needed
        const currentStyles = {
          position: formRef.current.style.position,
          top: formRef.current.style.top,
          left: formRef.current.style.left,
          width: formRef.current.style.width,
          transform: formRef.current.style.transform
        };
        
        // Store these styles as a data attribute
        formRef.current.setAttribute('data-saved-styles', JSON.stringify(currentStyles));
      }
    };
    
    // Listen for textarea focus events that might affect this form
    document.addEventListener('focus', (e) => {
      if (e.target.tagName === 'TEXTAREA' && formRef.current && formRef.current.contains(e.target)) {
        handleTextareaFocusForForm();
      }
    }, true);
      return () => {
      document.removeEventListener('focus', handleTextareaFocusForForm, true);
    };
  }, [isMobile]);
    // Don't show the minimized version on mobile
  if (!isVisible && !isMobile) {
    return (
      <div 
        className={`course-contact-minimized ${isFixed ? 'fixed' : ''}`}
        ref={formRef}
      >
        <button 
          className="minimized-button"
          onClick={() => setIsVisible(true)}
        >
          Have a Question?
        </button>
      </div>
    );
  }
  
  if (showThankYou) {
    return (
      <div 
        className={`course-contact-form ${isFixed && !isMobile ? 'fixed' : ''} ${isMobile ? 'mobile-form' : ''}`}
        ref={formRef}
      >
        <button 
          className="close-form-button" 
          onClick={() => setIsVisible(false)}
          aria-label="Close form"
        >
          ×
        </button>
        <div className="form-header">
          <h3>Thank You!</h3>
        </div>
        <div className="form-content">
          <div className="thank-you-message">
            <p>We've received your question and will get back to you shortly.</p>
          </div>
        </div>
      </div>
    );  }
    return (
    <div 
      className={`course-contact-form ${isFixed && !isMobile ? 'fixed' : ''} ${isMobile ? 'mobile-form' : ''}`}
      ref={formRef}
      onClick={handleFormContainerClick}
    >
      <button 
        className="close-form-button" 
        onClick={() => setIsVisible(false)}
        aria-label="Close form"
      >
        ×
      </button>
      <div className="form-header">
        <h3>Do You Have a Question?</h3>
      </div>
      <div className="form-content">        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name*</label><input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={(e) => handleChange(e, setFormData)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                required
              />            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">Last Name*</label><input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={(e) => handleChange(e, setFormData)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                required
              />
            </div>          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email Address*</label><input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={(e) => handleChange(e, setFormData)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
            />          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number*</label><input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => handleChange(e, setFormData)}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />            </div>
            
            <div className="form-group">
              <label htmlFor="companyName">Company Name</label><input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={(e) => handleChange(e, setFormData)}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>          </div>
          
          <div className="form-group">
            <label htmlFor="message">How Can We Help?*</label><textarea
              ref={textareaRef}
              id="message"
              name="message"
              value={formData.message}
              onChange={(e) => handleTextareaChange(e, setFormData)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              rows="4"
              required
            ></textarea>
          </div>
          
          <button type="submit" className="submit-button">Send Message</button>
        </form>
      </div>
    </div>
  );
};

export default CourseContactForm;